import {
  Metric,
  MetricType,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { TFunction } from 'i18next';
import {
  SeriesXrangeOptions,
  XrangePointOptionsObject,
  YAxisOptions,
} from 'highcharts';
import { EventConveyorSystemModeValue } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/event_pb';
import { DataPoint, mapDataPoint } from '~/shared/models/metrics/metrics-utils';
import { theme } from '~/shared/theme/theme';

const SYSTEM_MODE_CHART_SERIE_ID = 'systemMode';

const SYSTEM_MODE_CHART_HEIGHT = 40;

export class SystemModeMetricModel {
  chartHeight = SYSTEM_MODE_CHART_HEIGHT;

  type: MetricType = MetricType.SYSTEM_MODE;

  chartData: XrangePointOptionsObject[] = [];

  constructor(metric: Metric) {
    const dataPoints = metric.dataPoints.map((dataPoint) =>
      mapDataPoint(dataPoint),
    );

    this.chartData = SystemModeMetricModel.prepareXRangeData(dataPoints);
  }

  private static prepareXRangeData(
    data: DataPoint[],
  ): XrangePointOptionsObject[] {
    const result: XrangePointOptionsObject[] = [];
    let previousValue: null | number = null;

    data.forEach((point) => {
      const [time, value] = point;

      if (value === 0) {
        return;
      }

      if (value !== previousValue) {
        if (previousValue !== null) {
          result[result.length - 1].x2 = time;
        }

        result.push({
          x: time,
          x2: undefined,
          y: 0,
          name: value.toString(),
          color: SystemModeMetricModel.getSystemModeColor(value),
        });

        previousValue = value;
      }
    });

    // Set the last range to extend to now, as it represents the current state
    // The last dataPoint is a state change that continues until another point arrives
    if (result.length > 0 && result[result.length - 1].x2 === undefined) {
      result[result.length - 1].x2 = Date.now();
    }

    return result;
  }

  private static getSystemModeColor(
    value: EventConveyorSystemModeValue,
  ): string {
    switch (value) {
      case EventConveyorSystemModeValue.OUTSIDE_GROWING:
        return theme.palette.secondary.light;
      case EventConveyorSystemModeValue.GROWING:
        return theme.palette.success.main;
      case EventConveyorSystemModeValue.FREEZING_PROTECTION:
        return theme.palette.text.primary;
      case EventConveyorSystemModeValue.MANUAL:
        return theme.palette.error.main;
      default:
        return '#D3D3D3';
    }
  }

  private static getSystemModeLabel(
    t: TFunction,
    value: EventConveyorSystemModeValue,
  ): string {
    switch (value) {
      case EventConveyorSystemModeValue.OUTSIDE_GROWING:
        return t('system.mode.outsideGrowing');
      case EventConveyorSystemModeValue.GROWING:
        return t('system.mode.growing');
      case EventConveyorSystemModeValue.FREEZING_PROTECTION:
        return t('system.mode.freezingProtection');
      case EventConveyorSystemModeValue.MANUAL:
        return t('system.mode.manual');
      default:
        return t('system.mode.unspecified');
    }
  }

  // eslint-disable-next-line class-methods-use-this
  createSystemModeYAxis(t: TFunction): YAxisOptions {
    return {
      height: SYSTEM_MODE_CHART_HEIGHT,
      gridLineWidth: 0,
      opposite: false,
      title: {
        rotation: 0,
        reserveSpace: false,
        textAlign: 'right',
        x: -10,
        y: 5,
        style: {
          color: theme.palette.primary.dark,
          fontSize: '14px',
          lineHeight: '14px',
        },
        text: t('metrics.automation.title'),
      },
      labels: {
        enabled: false,
      },
      categories: [t('metrics.automation.title')],
      plotBands: [
        {
          from: -0.5,
          to: 0.5,
          color: theme.palette.background.default,
        },
      ],
    };
  }

  createSerie(t: TFunction, yIndex: number): SeriesXrangeOptions {
    return {
      type: 'xrange',
      data: this.chartData,
      id: SYSTEM_MODE_CHART_SERIE_ID,
      yAxis: yIndex,
      pointWidth: SYSTEM_MODE_CHART_HEIGHT,
      borderRadius: 0,
      showInNavigator: false,
      dataLabels: {
        enabled: true,
        formatter() {
          return SystemModeMetricModel.getSystemModeLabel(t, +this.name);
        },
        style: {
          fontSize: '14px',
          fontFamily: 'Noto Sans',
          fontWeight: 'bold',
          color: 'white',
        },
      },
      enableMouseTracking: false,
      states: {
        inactive: {
          opacity: 1,
        },
      },
      groupPadding: 0.5,
    };
  }
}
